/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {FindNearbyRequestArgs} from '@ion/core/src/services/ArcGISUtils';
import {FindNearbyGeofence, FindNearbyType, LatLong} from '@ion/core/src/types/entities';
import {
  convertOdometer,
  getUserUnit,
  isImperial,
  isMetric
} from '@ion/core/src/units/helpers';
import {
  Unit
} from '@ion/core/src/units/units';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {IconButton} from '../../../components/buttons/IconButton';
import {PrimaryButton} from '../../../components/buttons/PrimaryButton';
import Chip from '../../../components/chip/Chip';
import Slider from '../../../components/slider/Slider';
import TextField from '../../../components/textfield/TextField';
import {MaterialBackIcon} from '../../../icons/MaterialBackIcon';
import {PopupCloseIcon} from '../../../icons/PopupCloseIcon';
import {dlsAlt, dlsColor, dlsTypography} from '../../../theme/theme';
import {Marks} from './constants';
import {getDistance} from './helper';
import styles from './FindNearby.module.scss';

interface FindNearbyProps {
  modelPopupOrigin?: 'bottom' | 'left' | 'right' | 'top';
  onFindNearyBy?: Function;
  onFindNearbyPopupStatusChange?: Function;
  onFindButtonStateChange?: Function;
  showFindNearByPopup?: boolean;
  assetData?: Array<object>;
  onFindButtonClick?: Function;
  geofenceCoordinates?: FindNearbyGeofence;
  locationCoordinates?: LatLong;
  typeToFindBy?: string;
  mapView?: __esri.MapView;
  includeHeader?: boolean;
}

const useStyles = makeStyles((theme) => ({
  popup : {
    boxSizing       : 'border-box',
    backgroundColor : dlsColor('white')
  },
  root : {
    display        : 'flex',
    justifyContent : 'center',
    flexFlow       : 'column',
    margin         : '35px 52px 30px'
  },
  rootDiv : {
    display        : 'flex',
    justifyContent : 'center',
    flexFlow       : 'column',
    margin         : '16px 29px'
  },
  chips : {
    display   : 'inline-flex',
    flexFlow  : 'flex-start',
    flexWrap  : 'wrap',
    width     : '100%',
    marginTop : '8%'
  },
  chipsAlt : {
    display   : 'inline-flex',
    flexFlow  : 'flex-start',
    flexWrap  : 'wrap',
    width     : '100%',
    marginTop : '12px'
  },
  divStyle : {
    display        : 'inline-flex',
    flexFlow       : 'row',
    justifyContent : 'space-between'
  },
  mobDivStyle : {
    display        : 'inline-flex',
    flexFlow       : 'row',
    justifyContent : 'space-between',
    marginBottom   : '-22px'
  },
  distance : {
    ...dlsAlt('components', 'findnearby', 'distance'),
    color : dlsColor('grey2')
  },
  distanceUOM : {
    ...dlsAlt('components', 'findnearby', 'distanceUOM'),
    color   : dlsColor('grey4'),
    padding : '4px 0 !important'
  },
  textBox : {
    '& input' : {
      paddingLeft : '8px'
    },
    [theme.breakpoints.down('sm')] : {
      '& input' : {
        paddingLeft : '5px'
      }
    },
    '& p' : {
      ...dlsAlt('components', 'findnearby', 'distanceUOM'),
      color   : dlsColor('grey4'),
      padding : '4px 0 !important'
    },
    top : '-6px'
  },
  underline : {
    '&&&&' : {
      '&:hover' : {
        '&:before,&:after' : {
          borderColor       : `${dlsColor('caBlue')} !important`,
          borderBottomWidth : '1px !important',
          transition        : 'none'
        }
      }
    }
  },
  underlineError : {
    '&::after' : {
      borderColor : theme.palette.error.main
    }
  },
  slider : {
    padding : '16px 0px !important'
  },
  sliderAlt : {
    padding : '27px 0px !important'
  },
  findButton : {
    width     : '100%',
    maxHeight : '40px !important',
    margin    : '8px auto -6px',
    fontSize  : '16px !important'
  },
  mobFindButton : {
    marginTop : '20px'
  },
  textInput : {
    width : '86px'
  },
  mobTextInput : {
    width : '84px'
  },
  breadcrumbContainer : {
    padding : '0 24px 0 6px'
  },
  inactiveCrumb : {
    color  : dlsColor('grey4'),
    cursor : 'pointer'
  },
  activeCrumb : {
    color : dlsColor('grey3')
  },
  itemMobileHeader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    padding        : '15px 10px',
    '& > *'        : {
      textTransform : 'uppercase',
      letterSpacing : '1px',
      fontWeight    : 'bold'
    },
    '& > *:last-child' : {
      textAlign : 'center !important'
    }
  },
  title : {
    ...dlsTypography('h4'),
    color      : dlsColor('grey1'),
    lineHeight : 0
  },
  mobileContainer : {
    backgroundColor : dlsColor('white'),
    paddingBottom   : '5px'
  }
}));

export const FindNearby = ({
  modelPopupOrigin = 'left',
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindButtonStateChange,
  showFindNearByPopup,
  assetData,
  onFindButtonClick,
  geofenceCoordinates,
  locationCoordinates,
  mapView,
  typeToFindBy,
  includeHeader
}: FindNearbyProps) => {
  const uom                     = getUserUnit();
  const distanceUOM             = isImperial(uom) ? 'miles' : 'kilometers';
  const classes                 = useStyles();
  const {isDesktop}             = useMedia();
  const [distance, setDistance] = useState(0);

  const label           = translate('tracking:breadcrumb.findNearby');
  const findNearbyLabel = translate('maps:findNearby.assets');

  const getArrowClass = () => {
    return styles[`modelPopUp-${modelPopupOrigin}-open`];
  };

  const [findNearbyChip, updateFindNearbyChip] = useState([{
    label      : null,
    isSelected : true
  }]);

  // This effect ensures the user's UOM has been loaded (which it has by the
  // time the assetData prop is populated) and thus can be used to determine
  // the initial distance state value.
  useEffect(() => {
    if (assetData || geofenceCoordinates || locationCoordinates) {
      setDistance(isImperial(uom) ? 10 : 20);
    }
  }, [assetData, geofenceCoordinates, locationCoordinates]);

  useEffect(function() {
    if (findNearbyLabel !== 'findnearby.assets') {
      updateFindNearbyChip([{
        label      : findNearbyLabel,
        isSelected : findNearbyChip[0]?.isSelected
      }]);
    }
  }, [findNearbyLabel]);

  const handleChipClick = (e, label) => {
    const nextState = findNearbyChip.map((chip) => {
      if (chip.label === label) {
        chip.isSelected = !chip.isSelected;
      }
      return chip;
    });
    updateFindNearbyChip(nextState);
  };

  const findNearbyTypeMap = {
    [FindNearbyType.geofence] : geofenceCoordinates,
    [FindNearbyType.location] : locationCoordinates
  };

  function handleButtonClick() {
    findNearbyChip.map((chip) => {
      if (chip?.isSelected) {
        const dist = isMetric(uom) ? convertOdometer(distance, uom, Unit.US, true) : distance;
        let {latitude, longitude} = assetData;
        if (!latitude && !longitude) {
          const {geometry} = assetData || {};
          if (geometry) {
            const {type, centroid} = geometry;
            if (type === 'polygon') {
              latitude  = centroid.latitude;
              longitude = centroid.longitude;
            }
          }
        }
        const params: FindNearbyRequestArgs = {
          latitude,
          longitude,
          miles : dist,
          label : label
        };
        onFindNearyBy(params);
        onFindButtonStateChange(true);
      }
      if (isDesktop) onFindNearbyPopupStatusChange(false);
      onFindButtonClick?.();
      mapView?.popup.close();
    });
  };

  function handleInputChange({target:{value}}) {
    const marks = Marks[uom];
    const length = marks.length;
    if (length) {
      const distance = getDistance(marks, value);
      setDistance(distance);
    }
  };

  const inputProperties = useMemo(() => ({
    classes : {
      root      : classes.distanceUOM,
      underline : classes.underline
    },
    endAdornment :
    <InputAdornment position='end'>{
      `${translate(`maps:findNearby.${distanceUOM}`)}`
    }
    </InputAdornment>
  }), []);

  const btnStyle = useMemo(() => ({
    maxHeight     : isDesktop ? '40px' : '60px',
    fontSize      : '16px',
    letterSpacing : '1px'
  }), [isDesktop]);

  const handleMobileFindNearbyClose = useCallback(function() {
    onFindNearbyPopupStatusChange(false);
  }, [onFindNearbyPopupStatusChange]);

  return (
    <div className={isDesktop ? classes.popup : classes.mobileContainer}>
      {!isDesktop && includeHeader && (
        <div className={classes.itemMobileHeader}>
          <span onClick={handleMobileFindNearbyClose}>
            <MaterialBackIcon />
          </span>
          <span className={classes.title}>
            {translate('maps:findNearby.header')}
          </span>
          <IconButton onClick={handleMobileFindNearbyClose} icon={<PopupCloseIcon size={16} />} />
        </div>
      )}
      <div className={isDesktop ? classes.root : classes.rootDiv}>
        <div className={isDesktop ? classes.divStyle : classes.mobDivStyle}>
          <div>
            <span id={'find-nearby-distance-label'} className={classes.distance}>
              {translate('maps:findNearby.distance')}
            </span>
          </div>
          <div style={{display: 'inline-flex', justifyContent: 'flex-end'}}>
            <div className={isDesktop ? classes.textInput : classes.mobTextInput}>
              <TextField label=''
                className={classes.textBox}
                type='number'
                value={distance}
                onChange={handleInputChange}
                InputProps={inputProperties}/>
            </div>
          </div>
        </div>
        <div>
          <Slider step={0.1}
            name={'find-nearby-slider'}
            marks={Marks[uom]}
            value={distance}
            max={isImperial(uom) ? 50 : 100}
            onSlide={function(event, value) { setDistance(value as number); }}
            className={isDesktop ? classes.slider : classes.sliderAlt}
            input />
        </div>
        <div className={isDesktop ? classes.chips : classes.chipsAlt}>
          {findNearbyChip.map((chip, index) => {
            const cb = function(e) {
              handleChipClick(e, chip.label);
            };
            return (
              <div key={`${chip.label}-${index}`} style={{marginBottom: '12px'}}>
                <Chip label={chip.label}
                  variant='outlined'
                  isSelected={chip.isSelected}
                  onClick={cb}
                  onDelete={cb} />
              </div>
            );
          }
          )}
        </div>
        <div className={isDesktop ? classes.findButton : classes.mobFindButton}>
          <PrimaryButton style={{...btnStyle}}
            disabled={distance === 0}
            onClick={handleButtonClick}
            padding={'0px'}
            fullWidth>
            {translate('maps:findNearby.button')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default FindNearby;
